<template>
  <div class="scm-page">
    <van-nav-bar
      :title="studentName + ' 的报班'"
      left-arrow
      @click-left="goBack"
      fixed
      placeholder
    />

    <!-- 已加入：固定展示完整一周，没报班的日子显示空态 -->
    <template v-if="joinedTotal > 0">
      <div class="scm-week" v-for="g in joinedByWeek" :key="g.week">
        <div class="scm-week-title">{{ g.week }}</div>
        <div class="scm-list">
          <template v-if="g.data.length">
            <div class="scm-item" v-for="c in g.data" :key="c.service_class_id">
              <div class="scm-main">
                <div class="scm-headline">
                  <span class="scm-name">{{ c.class_name }}</span>
                  <van-tag :type="courseTypeType(c.course_type)" plain size="medium">
                    {{ courseTypeText(c.course_type) }}
                  </van-tag>
                </div>
                <span class="scm-meta" v-if="c.service_time">{{ c.service_time }}</span>
              </div>
              <van-button
                size="small"
                type="danger"
                plain
                :disabled="quiting || refreshing"
                @click="quit(c)"
                >退出</van-button
              >
            </div>
          </template>
          <div class="scm-empty-day" v-else>当天暂无报班</div>
        </div>
      </div>
    </template>

    <van-empty v-else description="暂未报名任何班" />

    <div class="scm-footer">
      <van-button type="primary" block round @click="openJoin">加入班级</van-button>
    </div>

    <!-- 加入班级：从 notJoin 中选，按星期分组 -->
    <van-popup
      v-model:show="showJoin"
      round
      class="join-popup"
      :style="{ width: '85%', maxHeight: '70%' }"
    >
      <div class="join-head">
        <span class="join-title">选择课程</span>
        <van-icon name="cross" class="join-close" @click="showJoin = false" />
      </div>
      <div class="join-list">
        <template v-if="hasNotJoin">
          <div
            class="join-week"
            v-for="g in notJoinWeeks"
            :key="g.week"
            v-show="g.data.length"
          >
            <div class="join-week-title">{{ g.week }}</div>
            <div
              v-for="c in g.data"
              :key="c.service_class_id"
              class="join-item"
              @click="onPick(c)"
            >
              <div class="join-info">
                <div class="join-headline">
                  <span class="join-name">{{ c.class_name }}</span>
                  <van-tag :type="courseTypeType(c.course_type)" plain size="medium">
                    {{ courseTypeText(c.course_type) }}
                  </van-tag>
                </div>
                <span class="join-meta" v-if="c.service_time">{{ c.service_time }}</span>
              </div>
              <van-icon name="plus" class="add-icon" />
            </div>
          </div>
        </template>
        <van-empty v-else description="暂无可加入的班" />
      </div>
    </van-popup>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";
import { courseTypeText, courseTypeType } from "../utils/courseType";

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const studentId = route.query.studentId;
    const studentName = route.query.studentName || "学生";

    // join / notJoin 均为「按星期分组」数组：[{ week, data:[...] }]
    const joinedWeeks = ref([]);
    const notJoinWeeks = ref([]);
    const showJoin = ref(false);
    // 加入/退出/重拉列表三个忙标志：列表项是 div、退出按钮也不绑 disabled，只能靠锁拦
    const joining = ref(false);
    const quiting = ref(false);
    const refreshing = ref(false);

    const hasNotJoin = computed(() =>
      notJoinWeeks.value.some((g) => g.data && g.data.length)
    );

    // 固定展示完整一周（周一~周日），无论后端是否返回该天的分组
    const WEEK_ORDER = ["星期一", "星期二", "星期三", "星期四", "星期五", "星期六", "星期日"];

    // 把接口返回的 join 按 week 对齐到固定序列，没数据的那天 data 为空数组
    const joinedByWeek = computed(() => {
      const map = {};
      joinedWeeks.value.forEach((g) => {
        map[g.week] = g.data || [];
      });
      return WEEK_ORDER.map((w) => ({ week: w, data: map[w] || [] }));
    });

    // 已加入班级总数，用于区分"完全没报"与"有报但某些天为空"
    const joinedTotal = computed(() =>
      joinedByWeek.value.reduce((sum, g) => sum + (g.data ? g.data.length : 0), 0)
    );

    // 单接口同时返回 join(已加入) + notJoin(未加入)，直接拆分到两组，无需 mock 兜底
    // 返回是否成功，供加入/退出决定要不要弹提示
    const loadData = async () => {
      refreshing.value = true;
      try {
        Toast.loading({ message: "加载中...", forbidClick: true });
        const res = await api.getStudentClassList({ student_id: studentId });
        Toast.clear();
        if (res.data.code == 1001) {
          const result = res.data.result || {};
          joinedWeeks.value = result.join || [];
          notJoinWeeks.value = result.notJoin || [];
          return true;
        }
        Toast(res.data.message);
        return false;
      } catch (error) {
        Toast.clear();
        Toast("加载失败，请重试");
        return false;
      } finally {
        refreshing.value = false;
      }
    };

    // 忙时拦截：请求中/重拉中一律拦下并给提示，静默 return 会让用户以为按钮坏了
    const checkBusy = () => {
      if (joining.value || quiting.value || refreshing.value) {
        Toast("处理中，请稍候");
        return true;
      }
      return false;
    };

    const openJoin = () => {
      showJoin.value = true;
    };

    // 弹层点击后二次确认再加入，与「退出」保持一致的确认交互
    const onPick = (c) => {
      if (checkBusy()) return;
      Dialog.confirm({
        title: "加入班",
        message: "确定让 " + studentName + " 加入「" + c.class_name + "」吗？",
      })
        .then(() => {
          showJoin.value = false;
          join(c);
        })
        .catch(() => {});
    };

    // 加入：用 service_class_id（班级唯一标识，非 id），成功后重新拉取保证 join/notJoin 同步
    const join = async (c) => {
      joining.value = true;
      try {
        Toast.loading({ message: "处理中...", forbidClick: true });
        const res = await api.joinClass({
          sid: localStorage.getItem("sid"),
          student_id: studentId,
          service_class_id: c.service_class_id,
        });
        Toast.clear();
        if (res.data.code == 1001) {
          // 锁保持到列表重拉结束：否则重拉期间还能再点一次加入
          await loadData();
          Toast.success("已加入");
        } else {
          Toast(res.data.message || "加入失败");
        }
      } catch (error) {
        Toast.clear();
        Toast("加入失败，请重试");
      } finally {
        joining.value = false;
      }
    };

    // 退班是强状态操作，不做乐观更新，一律以重新拉取的服务器数据为准
    const quit = (c) => {
      if (checkBusy()) return;
      Dialog.confirm({
        title: "退出班",
        message: "确定让 " + studentName + " 退出「" + c.class_name + "」吗？",
      })
        .then(async () => {
          // 二次确认后可能已经变忙（比如上一个请求刚发起），再拦一次
          if (checkBusy()) return;
          quiting.value = true;
          try {
            Toast.loading({ message: "处理中...", forbidClick: true });
            const res = await api.quitClass({
              sid: localStorage.getItem("sid"),
              student_id: studentId,
              service_class_id: c.service_class_id,
            });
            Toast.clear();
            if (res.data.code == 1001) {
              await loadData();
              Toast.success("已退出");
            } else {
              Toast(res.data.message || "退出失败");
            }
          } catch (error) {
            Toast.clear();
            Toast("退出失败，请重试");
          } finally {
            quiting.value = false;
          }
        })
        .catch(() => {});
    };

    const goBack = () => router.back();

    onMounted(() => {
      loadData();
    });

    return {
      studentName,
      joinedByWeek,
      joinedTotal,
      notJoinWeeks,
      showJoin,
      hasNotJoin,
      quiting,
      refreshing,
      courseTypeText,
      courseTypeType,
      openJoin,
      onPick,
      join,
      quit,
      goBack,
    };
  },
};
</script>

<style lang="less" scoped>
.scm-page {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 80px;
}

.scm-week {
  margin-top: 8px;
}

.scm-week-title {
  padding: 12px 14px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.scm-list {
  padding: 4px 10px 6px;
}

.scm-empty-day {
  padding: 14px 15px;
  margin-bottom: 10px;
  background: #fff;
  border: 1px dashed #e5e5e5;
  border-radius: 10px;
  color: #bbb;
  font-size: 13px;
  text-align: left;
}

.scm-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 10px;
}

.scm-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.scm-headline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.scm-name {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  word-break: break-all;
}

.scm-meta {
  font-size: 12px;
  color: #999;
}

.scm-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 15px;
  background: #fff;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.06);
}

.join-popup {
  display: flex;
  flex-direction: column;
  max-height: 70%;
  overflow: hidden;
}

.join-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 15px;
  border-bottom: 1px solid #f0f0f0;
}

.join-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.join-close {
  font-size: 20px;
  color: #999;
}

.join-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 6px 10px 14px;
}

.join-week {
  margin-top: 6px;
}

.join-week-title {
  padding: 8px 2px 2px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.join-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 12px;
  margin-top: 8px;
  background: #f7f8fa;
  border-radius: 10px;
}

.join-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.join-headline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.join-meta {
  font-size: 12px;
  color: #999;
}

.join-name {
  font-size: 15px;
  color: #333;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.add-icon {
  font-size: 18px;
  color: var(--color-primary);
}
</style>
